import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Check, Layers, Power, PlusCircle, Eye, Wand2 } from "lucide-react";
import { toast } from "sonner";
import SimpleAlertWizard from "@/components/SimpleAlertWizard";

const INP = "bg-secondary/60 border border-border rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#17A2B8]";
const SEV = [["low", "Info"], ["medium", "Moyen"], ["high", "Critique"]];
const METRICS = [
  ["speed", "Vitesse (GPS)"],
  ["ignition", "Contact moteur"],
  ["stopped", "Véhicule à l'arrêt"],
  ["idling", "Marche au ralenti"],
  ["moving", "Déplacement confirmé"],
  ["battery", "Tension batterie"],
  ["gsm", "Signal GSM"],
  ["zone", "Zone (géofence)"],
  ["poi", "Point d'intérêt (POI)"],
  ["time_window", "Plage horaire"],
  ["weekday", "Jours de la semaine"],
];
const NUM_OPS = [[">", "supérieure à"], [">=", "≥"], ["<", "inférieure à"], ["<=", "≤"], ["==", "égale à"], ["!=", "différente de"]];
const DAYS = [["0", "Lun"], ["1", "Mar"], ["2", "Mer"], ["3", "Jeu"], ["4", "Ven"], ["5", "Sam"], ["6", "Dim"]];
const SCOPES = [["global", "Global (toutes sociétés)"], ["company", "Société"], ["pool", "Groupe"], ["vehicle", "Véhicule"]];

const emptyCond = () => ({ metric: "speed", op: ">", value: "" });
const emptyRule = (isAdmin) => ({
  name: "", message: "", severity: "medium", enabled: true,
  scope_type: isAdmin ? "global" : "company", scope_id: "", duration_s: 0,
  groups: [{ op: "AND", conditions: [emptyCond()] }],
});

const defaultsFor = (metric) => {
  if (metric === "speed" || metric === "gsm") return { op: metric === "speed" ? ">" : "==", value: "" };
  if (metric === "battery") return { op: "<", value: "" };
  if (metric === "ignition") return { op: "==", value: "on" };
  if (metric === "stopped" || metric === "idling") return { op: "==", value: "true" };
  if (metric === "moving") return { op: "==", value: "true" };
  if (metric === "zone" || metric === "poi") return { op: "inside", value: "" };
  if (metric === "time_window") return { op: "in", value: { start: "22:00", end: "05:00" } };
  if (metric === "weekday") return { op: "in", value: ["0", "1", "2", "3", "4"] };
  return { op: "", value: "" };
};

const totalConds = (r) => (r.groups || []).reduce((n, g) => n + (g.conditions?.length || 0), 0);

// Aperçu humain régénéré depuis le modèle (jamais source de vérité).
const fmtDur = (s) => {
  s = Math.max(0, Math.round(Number(s) || 0));
  if (!s) return "";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (h) return `${h} h${m ? " " + m + " min" : ""}`;
  if (m) return `${m} min`;
  return `${sec} s`;
};
const phrase = (c, zones, pois) => {
  const opTxt = (NUM_OPS.find((o) => o[0] === c.op) || ["", c.op])[1];
  switch (c.metric) {
    case "speed": return `la vitesse est ${opTxt} ${c.value || "?"} km/h`;
    case "gsm": return `le signal GSM est ${opTxt} ${c.value ?? "?"}`;
    case "battery": return `la tension batterie est ${opTxt} ${c.value !== "" && c.value != null ? (c.value / 1000).toFixed(1) : "?"} V`;
    case "ignition": return `le contact ${c.op === "!=" ? "n'est pas" : "est"} ${c.value === "on" ? "actif" : "coupé"}`;
    case "stopped": return `le véhicule ${c.value === "false" ? "n'est pas" : "est"} à l'arrêt`;
    case "idling": return `le moteur ${c.value === "false" ? "ne tourne pas" : "tourne"} au ralenti`;
    case "moving": return `le véhicule ${c.value === "false" ? "ne se déplace pas" : "se déplace"} (mouvement confirmé)`;
    case "zone": { const z = zones.find((x) => x.id === c.value); return `le véhicule est ${c.op === "outside" ? "hors de" : "dans"} la zone « ${z ? z.name : "?"} »`; }
    case "poi": { const p = pois.find((x) => x.id === c.value); return `le véhicule est ${c.op === "outside" ? "hors du" : "dans le"} POI « ${p ? p.name : "?"} »`; }
    case "time_window": return `l'heure est entre ${c.value?.start || "?"} et ${c.value?.end || "?"}`;
    case "weekday": { const names = (c.value || []).map((d) => (DAYS.find((x) => x[0] === String(d)) || ["", d])[1]).join(", "); return `le jour est ${c.op === "not_in" ? "hors de " : ""}${names || "?"}`; }
    default: return c.metric;
  }
};
const humanize = (r, zones, pois) => {
  const sev = (SEV.find((s) => s[0] === r.severity) || SEV[1])[1].toLowerCase();
  const groups = (r.groups || []).map((g) => {
    const parts = (g.conditions || []).map((c) => phrase(c, zones, pois));
    const joiner = g.op === "OR" ? " OU " : " ET ";
    return parts.length > 1 ? `(${parts.join(joiner)})` : parts[0] || "";
  }).filter(Boolean);
  let s = `Déclencher une alerte « ${sev} » lorsque ${groups.join(" ET ")}`;
  if (Number(r.duration_s) > 0) s += ` pendant au moins ${fmtDur(r.duration_s)}`;
  return s + ".";
};

export default function CompositeAlertsPanel({ onChange }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = !!user && ["admin", "gestionnaire"].includes(user.role);
  const [rules, setRules] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pools, setPools] = useState([]);
  const [pois, setPois] = useState([]);
  const [zones, setZones] = useState([]);
  const [editing, setEditing] = useState(null);
  const [wizard, setWizard] = useState(false);

  const load = useCallback(() => { api.get("/alert-rules").then((r) => setRules(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    load();
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/pools").then((r) => setPools(r.data)).catch(() => {});
    api.get("/pois").then((r) => setPois(r.data)).catch(() => {});
    api.get("/geofences").then((r) => setZones(r.data)).catch(() => {});
  }, [load]);

  const startNew = () => setEditing(emptyRule(isAdmin));
  const startEdit = (r) => {
    const groups = r.groups?.length ? r.groups : [{ op: "AND", conditions: (r.conditions?.length ? r.conditions : [emptyCond()]) }];
    const scope_type = r.scope_type || (r.company_id ? "company" : (r.vehicle_ids?.length === 1 ? "vehicle" : "global"));
    const scope_id = r.scope_id ?? (scope_type === "company" ? r.company_id : (scope_type === "vehicle" ? (r.vehicle_ids?.[0] || "") : ""));
    setEditing({ ...emptyRule(isAdmin), ...r, groups, scope_type, scope_id, message: r.message || "", duration_s: r.duration_s || 0 });
  };

  const setF = (k, v) => setEditing((s) => ({ ...s, [k]: v }));
  const setGroup = (gi, patch) => setEditing((s) => ({ ...s, groups: s.groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) }));
  const setCond = (gi, ci, patch) => setEditing((s) => ({ ...s, groups: s.groups.map((g, i) => i !== gi ? g : { ...g, conditions: g.conditions.map((c, j) => (j === ci ? { ...c, ...patch } : c)) }) }));
  const addCond = (gi) => setEditing((s) => ({ ...s, groups: s.groups.map((g, i) => (i === gi ? { ...g, conditions: [...g.conditions, emptyCond()] } : g)) }));
  const rmCond = (gi, ci) => setEditing((s) => ({ ...s, groups: s.groups.map((g, i) => (i === gi ? { ...g, conditions: g.conditions.filter((_, j) => j !== ci) } : g)) }));
  const addGroup = () => setEditing((s) => (s.groups.length >= 3 ? s : { ...s, groups: [...s.groups, { op: "AND", conditions: [emptyCond()] }] }));
  const rmGroup = (gi) => setEditing((s) => ({ ...s, groups: s.groups.filter((_, i) => i !== gi) }));
  const setMetric = (gi, ci, metric) => setCond(gi, ci, { metric, ...defaultsFor(metric) });
  const toggleDay = (gi, ci, d) => setEditing((s) => ({ ...s, groups: s.groups.map((g, i) => i !== gi ? g : { ...g, conditions: g.conditions.map((c, j) => { if (j !== ci) return c; const cur = new Set((c.value || []).map(String)); cur.has(d) ? cur.delete(d) : cur.add(d); return { ...c, value: Array.from(cur) }; }) }) }));

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Nom requis"); return; }
    if (totalConds(editing) === 0) { toast.error("Ajoutez au moins une condition"); return; }
    if (editing.scope_type !== "global" && !editing.scope_id) { toast.error("Choisissez une cible"); return; }
    const payload = {
      name: editing.name, message: editing.message?.trim() || null, severity: editing.severity,
      enabled: editing.enabled, scope_type: editing.scope_type,
      scope_id: editing.scope_type === "global" ? null : editing.scope_id,
      duration_s: Number(editing.duration_s) || 0,
      groups: editing.groups.map((g) => ({ op: g.op, conditions: g.conditions.map((c) => ({ metric: c.metric, op: c.op, value: c.value })) })),
    };
    try {
      if (editing.id) await api.put(`/alert-rules/${editing.id}`, payload);
      else await api.post("/alert-rules", payload);
      toast.success("Règle enregistrée");
      setEditing(null); load(); onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'enregistrement"); }
  };
  const del = async (id) => { try { await api.delete(`/alert-rules/${id}`); load(); onChange && onChange(); } catch { toast.error("Erreur suppression"); } };
  const toggleEnabled = async (r) => {
    try {
      const groups = r.groups?.length ? r.groups : [{ op: "AND", conditions: r.conditions || [] }];
      await api.put(`/alert-rules/${r.id}`, { name: r.name, message: r.message || null, severity: r.severity || "medium",
        enabled: !r.enabled, scope_type: r.scope_type || (r.company_id ? "company" : "global"),
        scope_id: r.scope_id ?? (r.company_id || null), duration_s: r.duration_s || 0, groups });
      load();
    } catch { toast.error("Erreur"); }
  };

  const targetLabel = (r) => {
    const st = r.scope_type || (r.company_id ? "company" : "global");
    if (st === "global") return "Global";
    if (st === "company") return "Société · " + (companies.find((c) => c.id === (r.scope_id || r.company_id))?.name || "?");
    if (st === "pool") return "Groupe · " + (pools.find((p) => p.id === r.scope_id)?.name || "?");
    if (st === "vehicle") return "Véhicule · " + (vehicles.find((v) => v.id === r.scope_id)?.name || "?");
    return "—";
  };

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden mb-5" data-testid="composite-panel">
      <SimpleAlertWizard open={wizard} onClose={() => setWizard(false)} onCreated={() => { load(); onChange && onChange(); }}
        onAdvanced={startNew} companies={companies} pools={pools} vehicles={vehicles} zones={zones} isAdmin={isAdmin} />
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h2 className="font-bold text-lg flex items-center gap-2"><Layers className="w-5 h-5 brand-teal" /> Mes alertes personnalisées</h2>
        {!editing && canManage && (
          <div className="flex items-center gap-2">
            <button onClick={() => setWizard(true)} data-testid="create-alert-btn" className="flex items-center gap-1 bg-[#17A2B8] hover:bg-[#1391a5] text-white text-sm font-bold px-4 py-2 rounded-md">
              <Plus className="w-4 h-4" /> Créer une alerte
            </button>
            <button onClick={startNew} data-testid="composite-add" className="flex items-center gap-1 border border-border hover:border-[#17A2B8] hover:text-[#17A2B8] text-muted-foreground text-xs font-semibold px-3 py-2 rounded-md">
              <Wand2 className="w-4 h-4" /> Mode avancé
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="p-4 space-y-4" data-testid="composite-editor">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Nom de la règle
              <input className={INP} value={editing.name} onChange={(e) => setF("name", e.target.value)} data-testid="rule-name" placeholder="Ex: Contact la nuit hors dépôt" />
            </label>
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Message (optionnel, max 120)
              <input className={INP} maxLength={120} value={editing.message} onChange={(e) => setF("message", e.target.value)} data-testid="rule-message" placeholder="Texte affiché dans l'alerte" />
            </label>
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Gravité
              <select className={INP} value={editing.severity} onChange={(e) => setF("severity", e.target.value)} data-testid="rule-severity">
                {SEV.map((s) => <option key={s[0]} value={s[0]}>{s[1]}</option>)}
              </select>
            </label>
            <label className="text-xs text-muted-foreground flex items-center gap-2 mt-5">
              <input type="checkbox" checked={editing.enabled} onChange={(e) => setF("enabled", e.target.checked)} data-testid="rule-enabled" /> Règle active
            </label>
          </div>

          {/* Cible */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Cible
              <select className={INP} value={editing.scope_type} onChange={(e) => setF("scope_type", e.target.value) || setF("scope_id", "")} data-testid="rule-scope-type">
                {SCOPES.filter((s) => isAdmin || s[0] !== "global").map((s) => <option key={s[0]} value={s[0]}>{s[1]}</option>)}
              </select>
            </label>
            {editing.scope_type === "company" && (
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Société
                <select className={INP} value={editing.scope_id} onChange={(e) => setF("scope_id", e.target.value)} data-testid="rule-scope-company">
                  <option value="">— choisir —</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
            )}
            {editing.scope_type === "pool" && (
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Groupe
                <select className={INP} value={editing.scope_id} onChange={(e) => setF("scope_id", e.target.value)} data-testid="rule-scope-pool">
                  <option value="">— choisir —</option>{pools.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
            )}
            {editing.scope_type === "vehicle" && (
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Véhicule
                <select className={INP} value={editing.scope_id} onChange={(e) => setF("scope_id", e.target.value)} data-testid="rule-scope-vehicle">
                  <option value="">— choisir —</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.plate}</option>)}
                </select>
              </label>
            )}
          </div>

          {/* Groupes de conditions */}
          <div className="space-y-3" data-testid="rule-groups">
            {editing.groups.map((g, gi) => (
              <div key={gi} className="border border-border rounded-md p-3 bg-secondary/30" data-testid={`group-${gi}`}>
                <div className="flex items-center justify-between mb-2">
                  {gi > 0 && <span className="text-[11px] font-bold text-[#F5821F]">ET</span>}
                  <div className="flex items-center gap-2">
                    <span className="label-mono">Groupe {gi + 1}</span>
                    <select className={`${INP} py-1`} value={g.op} onChange={(e) => setGroup(gi, { op: e.target.value })} data-testid={`group-op-${gi}`}>
                      <option value="AND">Toutes vraies (ET)</option><option value="OR">Au moins une (OU)</option>
                    </select>
                  </div>
                  {editing.groups.length > 1 && <button onClick={() => rmGroup(gi)} className="text-[#EF4444] p-1" data-testid={`group-del-${gi}`}><Trash2 className="w-4 h-4" /></button>}
                </div>
                <div className="space-y-2">
                  {g.conditions.map((c, ci) => (
                    <div key={ci} className="flex flex-wrap items-center gap-2 border border-border/60 rounded-md p-2 bg-card" data-testid={`cond-${gi}-${ci}`}>
                      {ci > 0 && <span className="text-[10px] font-bold text-[#17A2B8] px-1">{g.op === "OR" ? "OU" : "ET"}</span>}
                      <select className={INP} value={c.metric} onChange={(e) => setMetric(gi, ci, e.target.value)} data-testid={`cond-metric-${gi}-${ci}`}>
                        {METRICS.map((m) => <option key={m[0]} value={m[0]}>{m[1]}</option>)}
                      </select>
                      {(c.metric === "speed" || c.metric === "gsm") && (<>
                        <select className={INP} value={c.op} onChange={(e) => setCond(gi, ci, { op: e.target.value })} data-testid={`cond-op-${gi}-${ci}`}>
                          {NUM_OPS.map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
                        </select>
                        <input type="number" className={`${INP} w-24`} value={c.value} onChange={(e) => setCond(gi, ci, { value: e.target.value })} placeholder={c.metric === "speed" ? "km/h" : "0-31"} data-testid={`cond-value-${gi}-${ci}`} />
                      </>)}
                      {c.metric === "battery" && (<>
                        <select className={INP} value={c.op} onChange={(e) => setCond(gi, ci, { op: e.target.value })} data-testid={`cond-op-${gi}-${ci}`}>
                          {NUM_OPS.map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
                        </select>
                        <input type="number" step="0.1" className={`${INP} w-24`} value={c.value === "" || c.value == null ? "" : c.value / 1000}
                          onChange={(e) => setCond(gi, ci, { value: e.target.value === "" ? "" : Math.round(parseFloat(e.target.value) * 1000) })}
                          placeholder="Volts" data-testid={`cond-value-${gi}-${ci}`} />
                        <span className="text-[11px] text-muted-foreground">V</span>
                      </>)}
                      {c.metric === "ignition" && (
                        <select className={INP} value={c.value} onChange={(e) => setCond(gi, ci, { value: e.target.value })} data-testid={`cond-value-${gi}-${ci}`}>
                          <option value="on">mis (ON)</option><option value="off">coupé (OFF)</option>
                        </select>
                      )}
                      {(c.metric === "stopped" || c.metric === "idling" || c.metric === "moving") && (
                        <select className={INP} value={c.value} onChange={(e) => setCond(gi, ci, { value: e.target.value })} data-testid={`cond-value-${gi}-${ci}`}>
                          <option value="true">Vrai</option><option value="false">Faux</option>
                        </select>
                      )}
                      {(c.metric === "zone" || c.metric === "poi") && (<>
                        <select className={INP} value={c.op} onChange={(e) => setCond(gi, ci, { op: e.target.value })} data-testid={`cond-op-${gi}-${ci}`}>
                          <option value="inside">dans</option><option value="outside">hors de</option>
                        </select>
                        <select className={INP} value={c.value} onChange={(e) => setCond(gi, ci, { value: e.target.value })} data-testid={`cond-value-${gi}-${ci}`}>
                          <option value="">— {c.metric === "zone" ? "zone" : "POI"} —</option>
                          {(c.metric === "zone" ? zones : pois).map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                        </select>
                      </>)}
                      {c.metric === "time_window" && (<>
                        <input type="time" className={INP} value={c.value?.start || ""} onChange={(e) => setCond(gi, ci, { value: { ...c.value, start: e.target.value } })} data-testid={`cond-start-${gi}-${ci}`} />
                        <span className="text-[11px] text-muted-foreground">→</span>
                        <input type="time" className={INP} value={c.value?.end || ""} onChange={(e) => setCond(gi, ci, { value: { ...c.value, end: e.target.value } })} data-testid={`cond-end-${gi}-${ci}`} />
                      </>)}
                      {c.metric === "weekday" && (<>
                        <select className={INP} value={c.op} onChange={(e) => setCond(gi, ci, { op: e.target.value })} data-testid={`cond-op-${gi}-${ci}`}>
                          <option value="in">parmi</option><option value="not_in">sauf</option>
                        </select>
                        <div className="flex gap-1">
                          {DAYS.map((d) => {
                            const on = (c.value || []).map(String).includes(d[0]);
                            return <button type="button" key={d[0]} onClick={() => toggleDay(gi, ci, d[0])} data-testid={`cond-day-${gi}-${ci}-${d[0]}`}
                              className={`text-[10px] px-1.5 py-1 rounded border ${on ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground"}`}>{d[1]}</button>;
                          })}
                        </div>
                      </>)}
                      {g.conditions.length > 1 && <button onClick={() => rmCond(gi, ci)} className="text-[#EF4444] ml-auto p-1" data-testid={`cond-del-${gi}-${ci}`}><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  ))}
                  {totalConds(editing) < 10 && (
                    <button onClick={() => addCond(gi)} data-testid={`cond-add-${gi}`} className="text-[#17A2B8] text-xs flex items-center gap-1 hover:underline"><PlusCircle className="w-3.5 h-3.5" /> Ajouter une condition</button>
                  )}
                </div>
              </div>
            ))}
            {editing.groups.length < 3 && (
              <button onClick={addGroup} data-testid="group-add" className="text-[#F5821F] text-xs font-semibold flex items-center gap-1 hover:underline"><PlusCircle className="w-4 h-4" /> Ajouter un groupe (ET)</button>
            )}
          </div>

          {/* Durée */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="label-mono">Pendant</span>
            <input type="number" min="0" className={`${INP} w-24`} value={Math.round((editing.duration_s || 0) / 60)}
              onChange={(e) => setF("duration_s", (parseInt(e.target.value, 10) || 0) * 60)} data-testid="rule-duration" />
            <span className="text-xs text-muted-foreground">minutes (0 = immédiat)</span>
          </div>

          {/* Aperçu humain */}
          <div className="border border-[#17A2B8]/40 bg-[#17A2B8]/10 rounded-md p-3 text-sm flex gap-2" data-testid="rule-preview">
            <Eye className="w-4 h-4 text-[#17A2B8] shrink-0 mt-0.5" />
            <span>{humanize(editing, zones, pois)}</span>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button onClick={save} data-testid="rule-save" className="bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold px-5 py-2 rounded-md text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Enregistrer</button>
            <button onClick={() => setEditing(null)} data-testid="rule-cancel" className="text-muted-foreground px-4 py-2 text-sm hover:text-foreground">Annuler</button>
          </div>
        </div>
      ) : (
        <div className="divide-y divide-border" data-testid="composite-list">
          <div className="px-4 py-2 text-[12px] text-muted-foreground bg-[#17A2B8]/5 border-b border-border" data-testid="native-alerts-hint">
            Les alertes simples (excès de vitesse, arrêt, ralenti, batterie, déconnexion) sont enregistrées sur le périmètre choisi et se gèrent via <b>« Gérer les alertes »</b>. Les règles ci-dessous sont vos alertes personnalisées (zone, horaires, contact, combinées).
          </div>
          {rules.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted-foreground">Aucune règle personnalisée. Cliquez sur « Créer une alerte » pour démarrer.</div>}
          {rules.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid={`rule-item-${r.id}`}>
              <button onClick={() => toggleEnabled(r)} title={r.enabled ? "Active" : "Inactive"} data-testid={`rule-toggle-${r.id}`}
                className={`p-1.5 rounded-md ${r.enabled ? "text-[#10B981] bg-[#10B981]/15" : "text-muted-foreground bg-secondary"}`}><Power className="w-4 h-4" /></button>
              <button onClick={() => startEdit(r)} data-testid={`rule-edit-${r.id}`} className="flex-1 text-left min-w-0">
                <div className="font-semibold text-sm truncate flex items-center gap-2">{r.name}
                  <span className="text-[10px] text-muted-foreground font-normal">v{r.version || 1} · {targetLabel(r)}</span>
                </div>
                <div className="text-xs text-muted-foreground truncate">{humanize({ ...r, groups: r.groups?.length ? r.groups : [{ op: "AND", conditions: r.conditions || [] }] }, zones, pois)}</div>
              </button>
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded shrink-0" style={{ background: "#F5821F22", color: "#F5821F" }}>{(SEV.find((s) => s[0] === r.severity) || SEV[1])[1]}</span>
              <button onClick={() => del(r.id)} data-testid={`rule-del-${r.id}`} className="text-[#EF4444] p-1.5 hover:bg-[#EF4444]/15 rounded shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
